import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  OnInit,
  DestroyRef,
  Inject,
  signal,
  computed,
  Optional,
} from '@angular/core';
import { FieldValidation } from '../../field-validation';
import { CrmDependencies, CrmInjectionToken, ObjectDependencyKey, ObjectType } from '../../../../tokens-and-interfaces';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { CrmModelDrivenFormErrorComponent } from '../model-driven-form-error/model-driven-form-error.component';
import { FieldWarningService } from '../model-driven-form-error/field-warning.service';
import { InlineEditService } from '../../inline-edit.service';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { debounceTime, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'crm-currency-renderer',
  templateUrl: './currency.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['../renderers.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyFormFieldModule,
    GalaxyInputModule,
    GalaxyPipesModule,
    MatInputModule,
    ReactiveFormsModule,
    DigitOnlyModule,
    CrmModelDrivenFormErrorComponent,
  ],
  providers: [FieldWarningService],
})
export class CurrencyRendererComponent implements OnInit {
  fieldId!: string;
  objectType!: ObjectType;
  control!: FormControl<number>;
  currencyCode!: string;
  label!: string;
  validation!: FieldValidation;
  readOnly!: boolean;

  private defaultCurrencyCode = signal<string>('');
  protected displayedCurrencyCode = computed(() => this.currencyCode || this.defaultCurrencyCode() || 'USD');

  constructor(
    private readonly inlineEditService: InlineEditService,
    private readonly destroyRef: DestroyRef,

    @Optional()
    @Inject(CrmInjectionToken)
    private readonly crmDependencies: CrmDependencies,
  ) {}

  ngOnInit(): void {
    const objectType = String(this.objectType).toLowerCase() as ObjectDependencyKey;
    const defaultFormValue$ = this.crmDependencies?.[objectType]?.formDefaultOnCreateInputValues$;
    if (defaultFormValue$) {
      firstValueFrom(defaultFormValue$).then((defaultFormValues) => {
        if (!defaultFormValues?.length) {
          return;
        }
        const defaultCurrencyCode = defaultFormValues.find((v) => v.fieldId === this.fieldId)?.value?.currencyCode;
        if (defaultCurrencyCode) {
          this.defaultCurrencyCode.set(defaultCurrencyCode);
        } else {
          console.warn(`No default currency code found for field ${this.fieldId}`);
        }
      });
    }

    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1000)).subscribe(() => {
      this.saveField();
    });
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, [
      {
        fieldId: this.fieldId,
        currencyValue: {
          value: this.control.value,
          currencyCode: this.displayedCurrencyCode(),
        },
      },
    ]);
  }
}
